import { useSyncExternalStore } from 'react';
import { electronInvoke } from '@/lib/electron-bridge';

/** A still the Desktop sends: a driven tab after an action, a Mac window's screenshot. */
type Still = { id: string; jpeg: Uint8Array; width: number; height: number };

const urls = new Map<string, string>();
/** A still removed stays until its item's exit animation is over. */
const FORGET_DELAY_MS = 1000;
const listeners = new Set<() => void>();

function receive(still: Still): void {
  const previous = urls.get(still.id);
  urls.set(
    still.id,
    URL.createObjectURL(new Blob([still.jpeg as BlobPart], { type: 'image/jpeg' })),
  );
  if (previous !== undefined) URL.revokeObjectURL(previous);
  for (const listener of listeners) listener();
}

/**
 * Follows the stills the Desktop sends to this page; `load` reads those it already has (the main
 * window's page, which may open after them). Returns the unfollow.
 */
export function followPipStills(load: boolean): () => void {
  const stop = window.crowdexDesktop?.listen('picture-in-picture-frame', (payload) =>
    receive(payload as Still),
  );
  // The floating page's window lives on: the Desktop tells it which stills are gone.
  const stopRemoved = window.crowdexDesktop?.listen(
    'picture-in-picture-still-removed',
    (payload) => {
      const { id } = payload as { id: string };
      setTimeout(() => forget(id), FORGET_DELAY_MS);
    },
  );
  if (load)
    void electronInvoke<Still[]>('pip_stills')
      .then((stills) => {
        for (const still of stills) receive(still);
      })
      .catch(() => undefined);
  return () => {
    stop?.();
    stopRemoved?.();
  };
}

function forget(id: string): void {
  const url = urls.get(id);
  if (url === undefined) return;
  URL.revokeObjectURL(url);
  urls.delete(id);
  for (const listener of listeners) listener();
}

/** Forgets the stills of items no longer shown. */
export function keepPipStills(ids: ReadonlySet<string>): void {
  let changed = false;
  for (const [id, url] of urls) {
    if (ids.has(id)) continue;
    URL.revokeObjectURL(url);
    urls.delete(id);
    changed = true;
  }
  if (changed) for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The item's latest still, as an object URL. */
export function usePipStill(id: string): string | undefined {
  return useSyncExternalStore(
    subscribe,
    () => urls.get(id),
    () => undefined,
  );
}
